const RETRY_REFRESH_IN_MS = 60_000;
const MINIMUM_TIME_TO_REFRESH_IN_SECONDS = 60; // Limit refreshes in case of skewed clocks

const currentTimeInSeconds = () => Math.floor(Date.now() / 1_000);
const autoRefreshEnabled = true;

const Key = {
  authLoading: 'auth.loading',
  authSession: 'auth.session',
  authError: 'auth.error',
};

const PersistentKey = {
  authSession: 'wg_integration.auth.session',
};

export const AuthService = (props, { getState, setState, subscribe }) => {
  let autoRefreshTimer = null;

  const clearSession = () => {
    setState(Key.authLoading, false);
    setState(Key.authSession, null);
    localStorage.removeItem(PersistentKey.authSession);
  };

  const refreshSession = async () => {
    const session = getState(Key.authSession);
    if (!session || !session.refresh) {
      return false;
    }

    const response = await fetch('/api/refresh', {
      method: 'POST',
      body: '{}',
      headers: {
        Authorization: 'Bearer ' + session.refresh,
      },
    });
    if (response.ok) {
      const session = await response.json();
      saveSession(session);
      return true;
    } else if (response.status === 401) {
      clearSession();
      return false;
    } else {
      throw Error('server error');
    }
  };

  const stopAutoRefresh = () => {
    if (autoRefreshTimer) {
      clearTimeout(autoRefreshTimer);
      autoRefreshTimer = null;
    }
  };

  const setupAutoRefresh = session => {
    stopAutoRefresh();
    if (autoRefreshEnabled && session && 'refresh_after' in session && 'issued_at' in session) {
      const maxRefreshInSeconds = session.refresh_after - session.issued_at;
      const refreshInSeconds = Math.min(
        maxRefreshInSeconds,
        session.refresh_after - currentTimeInSeconds(),
      );
      if (refreshInSeconds < MINIMUM_TIME_TO_REFRESH_IN_SECONDS) {
        console.log(`Refresh is below minimum time, refresh disabled [${refreshInSeconds}]`);
        return;
      }

      console.log(`Auto refresh in ${refreshInSeconds} seconds`);
      const attemptRefresh = async () => {
        autoRefreshTimer = null;
        try {
          console.log('Attempting refresh');
          await refreshSession();
        } catch (e) {
          // Retry refresh again in 1 minute
          autoRefreshTimer = setTimeout(async () => attemptRefresh(), RETRY_REFRESH_IN_MS);
        }
      };
      autoRefreshTimer = setTimeout(
        async () => attemptRefresh(),
        Math.floor(refreshInSeconds * 1_000),
      );
    }
  };

  const saveSession = session => {
    localStorage.setItem(PersistentKey.authSession, JSON.stringify(session));
    setState(Key.authSession, session);
    console.log('Saving session', session);
    setupAutoRefresh(session);
  };

  return {
    hooks: {
      onRegister: () => {
        const testingOptions = new URLSearchParams(location.search).get('testing');
        if (testingOptions === 'logout') {
          location.search = '';
          clearSession();
        }

        const savedSession = localStorage.getItem(PersistentKey.authSession);
        if (savedSession) {
          const session = JSON.parse(savedSession);
          setState(Key.authSession, session);
          setupAutoRefresh(session);
        }
      },
    },

    api: {
      isLoggedIn: () => !!getState(Key.authSession, null),
      isProcessing: () => getState(Key.authLoading, false),
      hasError: () => getState(Key.authError, '') !== '',
      lastError: () => getState(Key.authError, ''),
      login: async (username, password) => {
        setState(Key.authLoading, true);
        setState(Key.authSession, null);
        try {
          const response = await fetch('/api/login', {
            method: 'POST',
            body: JSON.stringify({ username, password }),
          });
          if (response.status === 200) {
            saveSession(await response.json());
          }
          if (response.status === 401) {
            setState(Key.authError, 'Incorrect username or password');
          } else if (response.status === 400) {
            setState(
              Key.authError,
              'Invalid details entered (password must be at least 6 characters)',
            );
          } else {
            setState(Key.authError, 'Server error');
          }
        } catch (error) {
          setState(Key.authError, error.message);
        } finally {
          setState(Key.authLoading, false);
        }
      },

      logout: () => {
        clearSession();
      },
      fetch: async ({ method, url, body }) => {
        const authFetch = async () => {
          const session = getState(Key.authSession);
          if (!session) {
            throw Error('Not authenticated');
          }
          return await fetch(url, {
            method: method ?? 'GET',
            body: body ? JSON.stringify(body) : undefined,
            headers: {
              Authorization: 'Bearer ' + session.access,
            },
          });
        };

        const response = await authFetch();
        if (response.ok) {
          return await response.json();
        }
        if (response.status !== 401) {
          throw Error(`Request failed [${response.status}]`);
        }

        if (!(await refreshSession())) {
          clearSession();
          throw Error('Not authenticated');
        }

        const retryResponse = await authFetch();
        if (retryResponse.ok) {
          return await retryResponse.json();
        }
        if (retryResponse.status === 401) {
          clearSession();
          throw Error('Not authenticated');
        }
        throw Error(`Request failed [${response.status}]`);
      },
    },
  };
};
